/**
 * i18n configuration.
 *
 * Boot strategy: language is decided ONCE at app boot in main.tsx
 * (see getBootLocale below) based on the URL ?lang= param for public
 * routes. Admin/builder UI stays in Hebrew unless explicitly switched.
 *
 * Changing language at runtime triggers a full page reload — this is
 * intentional, see the bilingual plan: it avoids re-mounting the entire
 * Emotion cache and MUI theme.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import he from './locales/he.json';
import en from './locales/en.json';

export type AppLocale = 'he' | 'en';

// Paths where ?lang=en is honored. Campaign pages, short-link campaigns, and
// the payment-success/complete page (which the Hyp/Tranzila iframe redirects
// to after a successful charge — must inherit the donor's locale so the
// thank-you screen stays in their language).
const PUBLIC_PATH_PREFIXES = ['/campaign/', '/c/', '/payments/'];

/**
 * Decides the boot locale based on URL.
 * - Only public campaign routes are eligible for ?lang= override.
 * - Everywhere else (admin/dashboard/builder) defaults to Hebrew.
 * - Missing or invalid ?lang= → Hebrew (backward-compatible default).
 */
export function getBootLocale(): AppLocale {
  if (typeof window === 'undefined') return 'he';
  const path = window.location.pathname || '';
  const isPublicRoute = PUBLIC_PATH_PREFIXES.some((p) => path.startsWith(p));
  if (!isPublicRoute) return 'he';
  const urlLang = new URLSearchParams(window.location.search).get('lang');
  return urlLang === 'en' ? 'en' : 'he';
}

export function getBootDirection(): 'rtl' | 'ltr' {
  return getBootLocale() === 'en' ? 'ltr' : 'rtl';
}

void i18n.use(initReactI18next).init({
  resources: {
    he: { translation: he },
    en: { translation: en },
  },
  lng: getBootLocale(),
  fallbackLng: 'he',
  interpolation: { escapeValue: false },
  returnNull: false,
});

export default i18n;
